import axios from "axios";
import { getValidToken } from "./authUtils";
import { UserPermissions } from "../types/operationType";

export interface ApiResponse<T> {
  isSuccess: boolean;
  data: T;
  message: string;
}

export const getPermissionMatrix = async () => {
  const token = await getValidToken();

  try {
    const response = await axios.get<ApiResponse<UserPermissions>>(
      `${process.env.REACT_APP_API_HOST}permissions/matrix`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.isSuccess) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to fetch permissions");
    }
  } catch (error) {
    console.error("Error fetching permission matrix:", error);
    return {};
  }
};

export const getPermissionSummary = async () => {
  const token = await getValidToken();

  try {
    const response = await axios.get<ApiResponse<UserPermissions>>(
      `${process.env.REACT_APP_API_HOST}permissions/summary`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.isSuccess) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || "Failed to fetch permissions");
    }
  } catch (error) {
    console.error("Error fetching permission matrix:", error);
    return {};
  }
};

// Helper functions for module access checks
export const hasDashboardAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return permissions?.canViewDashboard || false;
};

export const hasTripAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return permissions?.canViewTrips || false;
};

export const hasVehicleExpenseAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return permissions?.canViewVehicleExpense || false;
};

export const hasOtherExpenseAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return permissions?.canViewOtherExpense || false;
};

export const hasReportsAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return (
    permissions?.canViewTripPNL ||
    permissions?.canViewTripLedger ||
    permissions?.canViewIncomeReport ||
    permissions?.canViewExpenseReport ||
    permissions?.canViewFuelReport ||
    permissions?.canViewMaintenanceReport ||
    permissions?.canViewCommissionReport ||
    permissions?.canViewPickupReport ||
    permissions?.canViewDriverCleanerReport ||
    permissions?.canViewReportByVehicles ||
    false
  );
};

export const hasCompanyAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return permissions?.canViewCompany || false;
};

export const hasUserAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return permissions?.canViewUsers || false;
};

export const hasVehicleAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return permissions?.canViewVehicles || false;
};

export const hasVehicleFinanceAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return permissions?.canViewVehicleFinance || false;
};

export const hasVehicleMaintenanceAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return permissions?.canViewVehicleMaintenance || false;
};

export const hasVehicleDocumentAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return permissions?.canViewVehicleDocument || false;
};

export const hasVehicleInsuranceAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return permissions?.canViewVehicleInsurance || false;
};

export const hasVehiclePermitAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return permissions?.canViewVehiclePermit || false;
};

export const hasVehicleOdometerAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return permissions?.canViewVehicleOdometer || false;
};

export const hasMasterDataAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return (
    permissions?.canViewDriver ||
    permissions?.canViewCleaner ||
    permissions?.canViewLadyAttendant ||
    permissions?.canViewRoute ||
    permissions?.canViewServiceOperator ||
    permissions?.canViewParty ||
    permissions?.canViewBranch ||
    permissions?.canViewPickupMan ||
    permissions?.canViewPetrolPump ||
    permissions?.canViewParkingLocation ||
    permissions?.canViewGarage ||
    permissions?.canViewMechanic ||
    false
  );
};

export const hasBookingAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return permissions?.canViewBooking || false;
};

export const hasContractAccess = (
  permissions: UserPermissions | undefined
): boolean => {
  return permissions?.canViewContract || false;
};

// Legacy functions for backward compatibility (deprecated)
export const hasModuleAccess = (
  permissions: UserPermissions | undefined,
  module: string
): boolean => {
  if (!permissions) return false;

  switch (module) {
    case "DASHBOARD":
      return hasDashboardAccess(permissions);
    case "TRIP":
      return hasTripAccess(permissions);
    case "VEHICLE_EXPENSE":
      return hasVehicleExpenseAccess(permissions);
    case "OTHER_EXPENSE":
      return hasOtherExpenseAccess(permissions);
    case "REPORTS":
      return hasReportsAccess(permissions);
    case "COMPANY":
      return hasCompanyAccess(permissions);
    case "USER":
      return hasUserAccess(permissions);
    case "VEHICLE":
      return hasVehicleAccess(permissions);
    case "VEHICLE_FINANCE":
      return hasVehicleFinanceAccess(permissions);
    case "VEHICLE_MAINTENANCE":
      return hasVehicleMaintenanceAccess(permissions);
    case "VEHICLE_DOCUMENT":
      return hasVehicleDocumentAccess(permissions);
    case "VEHICLE_INSURANCE":
      return hasVehicleInsuranceAccess(permissions);
    case "VEHICLE_PERMIT":
      return hasVehiclePermitAccess(permissions);
    case "VEHICLE_ODOMETER":
      return hasVehicleOdometerAccess(permissions);
    case "MASTER_DATA":
      return hasMasterDataAccess(permissions);
    case "BOOKING":
      return hasBookingAccess(permissions);
    case "CONTRACT":
      return hasContractAccess(permissions);
    default:
      return false;
  }
};

export const getModulePermissions = (
  permissions: UserPermissions | undefined,
  module: string
): string[] => {
  // This function is deprecated since we now use boolean permissions
  // Return empty array for backward compatibility
  return [];
};

export const hasAnyPermission = (
  permissions: UserPermissions | undefined,
  module: string,
  requiredPermissions: string[]
): boolean => {
  // This function is deprecated since we now use boolean permissions
  // Use the specific permission check functions instead
  return hasModuleAccess(permissions, module);
};

export const hasAllPermissions = (
  permissions: UserPermissions | undefined,
  module: string,
  requiredPermissions: string[]
): boolean => {
  // This function is deprecated since we now use boolean permissions
  // Use the specific permission check functions instead
  return hasModuleAccess(permissions, module);
};
